import { useState } from "react";
import { toast } from "sonner";
import { Check, X, Loader2, CalendarPlus, ListTodo, Users, Target, Megaphone, CheckCircle2, AlertTriangle, Wallet, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/common";
import { api, errMsg, fmtDateTime } from "@/lib/api";

const ICONS = { calendar: CalendarPlus, tasks: ListTodo, hr: Users, goals: Target, finance: Wallet, marketing: Megaphone, seo: Search };

const BORDER = {
  pending: "border-l-[hsl(var(--warning))]",
  executed: "border-l-[hsl(var(--success))]",
  approved: "border-l-[hsl(var(--success))]",
  rejected: "border-l-[hsl(var(--destructive))]",
  failed: "border-l-[hsl(var(--destructive))]",
};

export const ProposalCard = ({ proposal, onChange, compact = false }) => {
  const [busy, setBusy] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const p = proposal;
  const Icon = p.type === "post_announcement" ? Megaphone : ICONS[p.module] || ListTodo;

  const approve = async () => {
    setBusy("approve");
    try {
      const { data } = await api.post(`/proposals/${p.action_id}/approve`);
      toast.success("Approved and done", { description: p.title });
      onChange?.(data);
    } catch (e) {
      toast.error(errMsg(e));
      try {
        const { data } = await api.get(`/proposals`, { params: { status: "all" } });
        const fresh = data.find((x) => x.action_id === p.action_id);
        if (fresh) onChange?.(fresh);
      } catch { /* ignore */ }
    } finally {
      setBusy(null);
    }
  };

  const reject = async () => {
    setBusy("reject");
    try {
      const { data } = await api.post(`/proposals/${p.action_id}/reject`, { reason });
      toast("Proposal rejected", { description: p.title });
      onChange?.(data);
      setRejectOpen(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="ai-proposal-card"
      data-status={p.status}
      className={`bg-card border border-border border-l-4 ${BORDER[p.status] || ""} rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-8 w-8 shrink-0 rounded-lg bg-accent flex items-center justify-center">
            <Icon className="h-4 w-4 text-accent-foreground" />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{p.label} · proposed by AI</p>
            <p className="text-sm font-semibold leading-snug break-words" data-testid="ai-proposal-title">{p.title}</p>
          </div>
        </div>
        <StatusBadge status={p.status} testid="ai-proposal-status" />
      </div>

      {!compact && p.details?.length > 0 && (
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-sm">
          {p.details.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-muted-foreground text-xs sm:text-sm">{k}</dt>
              <dd className="break-words whitespace-pre-wrap">{v}</dd>
            </div>
          ))}
        </dl>
      )}

      {p.status === "pending" ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" onClick={approve} disabled={!!busy} className="active:scale-[0.98]" data-testid="ai-proposal-approve-button">
            {busy === "approve" ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
            Approve
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setRejectOpen(true)} disabled={!!busy} data-testid="ai-proposal-reject-button">
            <X className="h-4 w-4 mr-1" /> Reject
          </Button>
        </div>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5" data-testid="ai-proposal-decision">
          {p.status === "executed" && <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--success))]" />}
          {p.status === "failed" && <AlertTriangle className="h-3.5 w-3.5 text-destructive" />}
          {p.status === "executed" ? "Approved & executed" : p.status === "failed" ? `Failed: ${p.error || ""}` : "Rejected"}
          {p.decided_by ? ` by ${p.decided_by}` : ""} {p.decided_at ? `· ${fmtDateTime(p.decided_at)}` : ""}
          {p.reason ? ` · “${p.reason}”` : ""}
        </p>
      )}

      <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <AlertDialogContent data-testid="ai-proposal-reject-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this proposal?</AlertDialogTitle>
            <AlertDialogDescription>{p.title}. Nothing will change. You can add a reason.</AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional reason" data-testid="ai-proposal-reject-reason" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ai-proposal-reject-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); reject(); }} className="bg-destructive text-white hover:bg-destructive/90" data-testid="ai-proposal-reject-confirm">
              {busy === "reject" && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
